/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card, Row, Col, Space, Flex, Table, InputNumber, Button,
  Typography, Tag, message, Modal, Alert, Divider,
} from "antd";
import {
  SaveOutlined, ArrowLeftOutlined, PrinterOutlined, ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import GoodsReceiptService from "../../../service/ERP/GoodsReceipt.service";

const grService = GoodsReceiptService();
const gotoFrom = "/ERP/goods-receipt";

const num = (v) => Number(v || 0);
const fdate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-");

/* ข้อความบาร์โค้ด : <lot>-<เลขกล่อง 2 หลัก> */
const makeBarcode = (lotNo, boxNo) =>
  `${lotNo || ""}-${String(boxNo).padStart(2, "0")}`;

/* แบ่งจำนวนออกเป็น n กล่องให้เท่าๆ กัน เศษไปกล่องสุดท้าย */
const splitQty = (total, boxes) => {
  const t = num(total);
  const n = Math.max(1, Math.floor(num(boxes)));
  const base = Math.floor((t / n) * 100) / 100;
  const arr = Array.from({ length: n }, () => base);
  const used = base * n;
  arr[n - 1] = Math.round((arr[n - 1] + (t - used)) * 100) / 100;
  return arr;
};

export default function TagsSplit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = location.state || {};
  const grcode = config?.code;

  const [loading, setLoading] = useState(false);
  const [gr, setGr] = useState({});
  const [lots, setLots] = useState([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!grcode) {
      navigate(gotoFrom, { replace: true });
      return;
    }
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await grService.tagsGet(grcode);
      const { gr: g, lots: ls, tags } = res.data?.data || {};
      setGr(g || {});

      /* รวม lot กับ tag ที่เคยแยกไว้ ถ้ายังไม่เคยแยก → เริ่มที่ 1 กล่อง */
      const rows = (ls || []).map((l, i) => {
        const saved = (tags || []).filter((t) => t.lot_no === l.lot_no);
        const boxes = saved.length > 0 ? saved.length : 1;
        const qtys =
          saved.length > 0
            ? saved.sort((a, b) => a.box_no - b.box_no).map((t) => num(t.qty))
            : splitQty(l.list_qty, 1);
        return {
          key: `${l.lot_no}-${i}`,
          lot_no: l.lot_no,
          stcode: l.stcode,
          stname: l.stname || l.stcode,
          unit: l.unit || "",
          list_qty: num(l.list_qty),
          mfg_date: l.mfg_date,
          expire_date: l.expire_date,
          boxes,
          qtys,
        };
      });
      setLots(rows);
      setDirty(false);
    } catch (err) {
      message.error(err?.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  /* เปลี่ยนจำนวนกล่องของ lot → แบ่งจำนวนใหม่ให้อัตโนมัติ */
  const changeBoxes = (key, boxes) => {
    setLots((prev) =>
      prev.map((r) =>
        r.key === key
          ? { ...r, boxes: Math.max(1, num(boxes)), qtys: splitQty(r.list_qty, boxes) }
          : r
      )
    );
    setDirty(true);
  };

  /* แก้จำนวนในกล่องใดกล่องหนึ่งเอง */
  const changeBoxQty = (key, boxIndex, value) => {
    setLots((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const qtys = [...r.qtys];
        qtys[boxIndex] = num(value);
        return { ...r, qtys };
      })
    );
    setDirty(true);
  };

  /* แบ่งเท่าๆ กันใหม่ทุก lot */
  const redistributeAll = () => {
    setLots((prev) => prev.map((r) => ({ ...r, qtys: splitQty(r.list_qty, r.boxes) })));
    setDirty(true);
    message.success("แบ่งจำนวนใหม่ให้เท่าๆ กันแล้ว");
  };

  /* ---- รายการ tag ที่จะได้ ---- */
  const tagList = useMemo(
    () =>
      lots.flatMap((r) =>
        r.qtys.map((q, i) => ({
          key: `${r.lot_no}-${i + 1}`,
          lot_no: r.lot_no,
          stcode: r.stcode,
          stname: r.stname,
          unit: r.unit,
          box_no: i + 1,
          box_total: r.boxes,
          qty: q,
          mfg_date: r.mfg_date,
          expire_date: r.expire_date,
          barcode: makeBarcode(r.lot_no, i + 1),
        }))
      ),
    [lots]
  );

  /* lot ที่ผลรวมกล่องไม่ตรงกับจำนวนที่รับ */
  const mismatched = useMemo(
    () =>
      lots.filter(
        (r) => Math.abs(r.qtys.reduce((s, q) => s + num(q), 0) - r.list_qty) > 0.001
      ),
    [lots]
  );

  const handleSave = async () => {
    if (mismatched.length > 0) {
      Modal.confirm({
        title: "จำนวนไม่ตรงกับที่รับ",
        content: `มี ${mismatched.length} lot ที่ผลรวมของกล่องไม่เท่ากับจำนวนที่รับ ต้องการบันทึกต่อหรือไม่?`,
        okText: "บันทึกต่อ",
        cancelText: "กลับไปแก้",
        onOk: () => doSave(),
      });
      return;
    }
    doSave();
  };

  const doSave = async () => {
    try {
      setLoading(true);
      await grService.tagsSave({ grcode, tags: tagList });
      message.success(`บันทึกการแยก Tags สำเร็จ (${tagList.length} ป้าย)`);
      setDirty(false);
    } catch (err) {
      message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  const openPrintWindow = () => {
    window.open(`/gr-tags-print/${encodeURIComponent(grcode)}`, "_blank");
  };

  /* พิมพ์ Tags : ต้องบันทึกก่อน เพราะหน้าพิมพ์อ่านจากฐานข้อมูล */
  const handlePrint = () => {
    if (tagList.length === 0) {
      message.warning("ยังไม่มีป้ายให้พิมพ์");
      return;
    }
    if (!dirty) {
      openPrintWindow();
      return;
    }
    Modal.confirm({
      title: "ข้อมูลมีการแก้ไข",
      content:
        "หน้าพิมพ์อ่านข้อมูลจากที่บันทึกไว้ ต้องการบันทึกการแยก Tags ก่อนพิมพ์หรือไม่?",
      okText: "บันทึกแล้วพิมพ์",
      cancelText: "พิมพ์ข้อมูลชุดเดิม",
      onOk: async () => {
        await doSave();
        openPrintWindow();
      },
      onCancel: () => openPrintWindow(),
    });
  };

  const handleBack = () => {
    if (!dirty) {
      navigate(gotoFrom, { replace: true });
      return;
    }
    Modal.confirm({
      title: "ข้อมูลมีการแก้ไข",
      content: "ต้องการบันทึกการแยก Tags ก่อนออกจากหน้านี้หรือไม่?",
      okText: "บันทึกแล้วออก",
      cancelText: "ออกโดยไม่บันทึก",
      onOk: async () => {
        await doSave();
        navigate(gotoFrom, { replace: true });
      },
      onCancel: () => navigate(gotoFrom, { replace: true }),
    });
  };

  /* ---------- ตารางแยกกล่องต่อ lot ---------- */
  const lotColumns = [
    { title: "Lot No.", dataIndex: "lot_no", width: 150 },
    { title: "รหัสสินค้า", dataIndex: "stcode", width: 120 },
    { title: "ชื่อสินค้า", dataIndex: "stname" },
    {
      title: "จำนวนที่รับ",
      dataIndex: "list_qty",
      width: 110,
      align: "right",
      render: (v, r) => `${num(v).toLocaleString()} ${r.unit || ""}`,
    },
    { title: "วันที่ผลิต", dataIndex: "mfg_date", width: 110, align: "center", render: fdate },
    { title: "วันหมดอายุ", dataIndex: "expire_date", width: 110, align: "center", render: fdate },
    {
      title: "แยกกี่กล่อง",
      dataIndex: "boxes",
      width: 120,
      align: "center",
      render: (v, r) => (
        <InputNumber
          min={1}
          max={999}
          value={v}
          style={{ width: "100%" }}
          onChange={(val) => changeBoxes(r.key, val)}
        />
      ),
    },
    {
      title: "จำนวนต่อกล่อง",
      width: 320,
      render: (_, r) => (
        <Flex gap={6} wrap="wrap">
          {r.qtys.map((q, i) => (
            <InputNumber
              key={i}
              size="small"
              min={0}
              value={q}
              style={{ width: 92 }}
              addonBefore={`${i + 1}/${r.boxes}`}
              onChange={(val) => changeBoxQty(r.key, i, val)}
            />
          ))}
        </Flex>
      ),
    },
    {
      title: "รวม",
      width: 110,
      align: "right",
      render: (_, r) => {
        const sum = r.qtys.reduce((s, q) => s + num(q), 0);
        const ok = Math.abs(sum - r.list_qty) <= 0.001;
        return (
          <Typography.Text type={ok ? "success" : "danger"} strong>
            {sum.toLocaleString()}
          </Typography.Text>
        );
      },
    },
  ];

  /* ---------- ตารางสรุป Tag ที่จะได้ ---------- */
  const tagColumns = [
    { title: "Lot No.", dataIndex: "lot_no", width: 150 },
    { title: "ชื่อสินค้า", dataIndex: "stname" },
    {
      title: "กล่อง",
      width: 90,
      align: "center",
      render: (_, r) => (
        <Tag color="blue">{r.box_no}/{r.box_total}</Tag>
      ),
    },
    {
      title: "จำนวน",
      dataIndex: "qty",
      width: 110,
      align: "right",
      render: (v, r) => `${num(v).toLocaleString()} ${r.unit || ""}`,
    },
    { title: "วันหมดอายุ", dataIndex: "expire_date", width: 110, align: "center", render: fdate },
    { title: "Barcode", dataIndex: "barcode", width: 180 },
  ];

  return (
    <div className="gr-tags-split">
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            แยก Tags สำหรับพิมพ์ Barcode
            {dirty && (
              <Tag color="orange" style={{ marginLeft: 8, verticalAlign: "middle" }}>
                ยังไม่ได้บันทึก
              </Tag>
            )}
          </Typography.Title>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              ย้อนกลับ
            </Button>
            <Button icon={<ThunderboltOutlined />} onClick={redistributeAll}>
              แบ่งเท่ากันใหม่
            </Button>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              style={{ borderColor: "#fa8c16", color: "#fff", background: "#fa8c16" }}
            >
              พิมพ์ Tags
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
              บันทึกการแยก
            </Button>
          </Space>
        </Flex>

        <Card loading={loading}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={8} md={5}>
              <Typography.Text type="secondary">เลขที่ใบรับสินค้า</Typography.Text>
              <div><Tag color="blue">{gr.grcode}</Tag></div>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Typography.Text type="secondary">ผู้จำหน่าย</Typography.Text>
              <div><strong>{gr.supname}</strong></div>
            </Col>
            <Col xs={24} sm={8} md={5}>
              <Typography.Text type="secondary">วันที่รับ</Typography.Text>
              <div>{fdate(gr.grdate)}</div>
            </Col>
            <Col xs={24} md={8}>
              <Typography.Text type="secondary">จำนวนป้ายที่จะได้</Typography.Text>
              <div><Tag color="green">{tagList.length} ป้าย</Tag></div>
            </Col>
          </Row>
        </Card>

        {mismatched.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`มี ${mismatched.length} lot ที่ผลรวมของกล่องไม่เท่ากับจำนวนที่รับ`}
            description="ตรวจสอบคอลัมน์ 'รวม' ที่เป็นสีแดง หรือกด 'แบ่งเท่ากันใหม่' เพื่อให้ระบบคำนวณให้"
          />
        )}

        <Card title="แยกกล่องต่อ Lot">
          <Table
            size="small"
            bordered
            rowKey="key"
            columns={lotColumns}
            dataSource={lots}
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: <span>ใบรับสินค้านี้ยังไม่มี Lot</span> }}
          />
        </Card>

        <Divider className="!my-0" />

        <Card title={`สรุป Tags ที่จะพิมพ์ (${tagList.length})`}>
          <Table
            size="small"
            bordered
            rowKey="key"
            columns={tagColumns}
            dataSource={tagList}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: <span>ยังไม่มีป้าย</span> }}
          />
        </Card>
      </Space>
    </div>
  );
}
